import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { tasksApiFor, type ITask } from '@/services/TasksApi';
import { describeLoadFailure, type ILoadFailure } from '@/services/loadFailure';
import { urgentCountOf } from '@/features/tasks/taskPriority';
import {
  createAttentionPoller,
  type AttentionPoller,
  type AttentionSubscriber,
} from './attentionPoller';

/**
 * What the current set is about: one work area, or every work area this reader may read (I-0560's
 * rule, reused for "Tasks for me" — I-0900).
 */
export const ALL_WORK_AREAS_SCOPE = 'all-work-areas';
/** Same convention as `ProjectStore`/`DecisionsStore` (F-0383, §10.9): one field, not a second. */
export const NAMESPACE_SCOPE_PREFIX = 'namespace:';
export type TasksScopeType = string | typeof ALL_WORK_AREAS_SCOPE;

/** A scope's rows and when they were read (RD-25, as `DecisionsStore`'s RD-23). */
interface ICarriedSet {
  waiting: ITask[];
  answeredAt: string | null;
}

export interface TasksStoreOptions {
  /**
   * F-0236's attention stream, extended with a `task` reason (I-0900): the server rings it
   * unconditionally — never gated by a notification preference — at each of the three sources: a
   * responsible change, a hold or move request created or resolved, and a question turn asked,
   * answered or claimed. This is the live channel `DecisionsStore` has for decision events; Tasks
   * has no cheaper narrow filter of its own (a decision event is one well-known kind, but almost any
   * work-item, hold or question-turn event could matter here), so it rides the one stream that
   * already reaches every scope in one subscription rather than a fleet of per-work-area listeners.
   */
  subscribeToAttention?: AttentionSubscriber;
}

/**
 * "Tasks for me" (I-0900): everything but decisions waiting on the reader, read the same way
 * `DecisionsStore` reads *Decisions for me* — one store, read by the sidebar badge and the page
 * beneath it, so the two numbers cannot disagree (mirrors DS-44).
 */
export class TasksStore {
  scope: TasksScopeType | null = null;
  /** `null` until the first read for this scope has answered. */
  waiting: ITask[] | null = null;
  failure: ILoadFailure | null = null;
  /**
   * I-1077: when `waiting` was last read successfully (ISO). A refresh that fails leaves the old set
   * on screen, and the failure banner says how old it is from this — never a stale list that looks
   * current. `null` while nothing has been read for the scope.
   */
  answeredAt: string | null = null;
  loading = false;

  private poller: AttentionPoller | null = null;
  private unsubscribeAttention: (() => void) | null = null;
  private requestSerial = 0;

  constructor(options: TasksStoreOptions = {}) {
    makeObservable(this, {
      scope: observable,
      waiting: observable.ref,
      failure: observable.ref,
      answeredAt: observable,
      loading: observable,
      workAreaId: computed,
      allWorkAreas: computed,
      namespaceId: computed,
      taskCount: computed,
      urgentCount: computed,
      unknown: computed,
      load: action,
      loadAllWorkAreas: action,
      loadNamespace: action,
      refresh: action,
      reset: action,
      enterWorkArea: action,
    });
    if (typeof window !== 'undefined') {
      this.poller = createAttentionPoller({ read: () => this.refresh() });
      // I-0900 TF-11: the row is on screen within a second of the ring, so the first ring after a
      // quiet spell reads at once; a burst — a hold or a question created and resolved in the same
      // writes, as DS-45/I-0635 found — still coalesces into one trailing read (`push`).
      this.unsubscribeAttention =
        options.subscribeToAttention?.({
          onChange: (change) => {
            if (change.reason !== 'read') this.poller?.push();
          },
          onResume: () => this.poller?.soon(0),
        }) ?? null;
    }
  }

  get workAreaId(): string | null {
    return this.scope === null || this.scope === ALL_WORK_AREAS_SCOPE || this.namespaceId !== null
      ? null
      : this.scope;
  }

  get allWorkAreas(): boolean {
    return this.scope === ALL_WORK_AREAS_SCOPE;
  }

  /** The namespace the current set is scoped to (F-0383, §10.9), or `null` outside that scope. */
  get namespaceId(): string | null {
    return this.scope?.startsWith(NAMESPACE_SCOPE_PREFIX)
      ? this.scope.slice(NAMESPACE_SCOPE_PREFIX.length)
      : null;
  }

  /** The badge's number: `undefined` while the first read is in flight. */
  get taskCount(): number | undefined {
    return this.waiting?.length;
  }

  /** I-2602 (TF-38): how many of them are urgent, the count's urgent part. */
  get urgentCount(): number | undefined {
    return this.waiting ? urgentCountOf(this.waiting) : undefined;
  }

  /** The first read failed and nothing is known: the badge says nothing rather than `...` forever. */
  get unknown(): boolean {
    return this.waiting === null && this.failure !== null;
  }

  async load(workAreaId: string): Promise<void> {
    await this.loadScope(workAreaId);
  }

  async loadAllWorkAreas(): Promise<void> {
    await this.loadScope(ALL_WORK_AREAS_SCOPE);
  }

  /** Read the set across one namespace's whole subtree (F-0383, §10.9) — one request, as above. */
  async loadNamespace(namespaceId: string): Promise<void> {
    await this.loadScope(`${NAMESPACE_SCOPE_PREFIX}${namespaceId}`);
  }

  /** Idempotent per scope, exactly like `DecisionsStore.load` (I-0587, AC-2). */
  private async loadScope(scope: TasksScopeType): Promise<void> {
    if (this.scope === scope) return;
    const carried = this.carriedInto(scope);
    this.scope = scope;
    this.waiting = carried?.waiting ?? null;
    this.failure = null;
    // Carried rows keep the age of the read they came from, so a failed re-read says how old.
    this.answeredAt = carried?.answeredAt ?? null;
    await this.refresh();
  }

  /** RD-25: the last set each scope answered this session, with when, for a return to the scope. */
  private readonly lastSets = new Map<TasksScopeType, ICarriedSet>();

  /**
   * RD-25 (I-1477, `docs/concept-mobile-app.md`): the rows a scope can show while its read is under
   * way — `DecisionsStore`'s RD-23 rule, for Tasks for me. Measured: a relayed scope moved this store
   * All → nothing → All → work area, and each move dropped the set.
   *
   * The scope's own last answer, if it had one; else, narrowing from All to a work area, that work
   * area's rows of the All set. Nothing otherwise: a narrower set cannot stand for a wider one, nor
   * one work area's for another's (I-0958, WS-3).
   */
  private carriedInto(scope: TasksScopeType): ICarriedSet | null {
    const own = this.lastSets.get(scope);
    if (own) return own;
    const all = this.lastSets.get(ALL_WORK_AREAS_SCOPE);
    if (!all || scope === ALL_WORK_AREAS_SCOPE || scope.startsWith(NAMESPACE_SCOPE_PREFIX)) {
      return null;
    }
    return {
      waiting: all.waiting.filter((task) => task.item.workAreaId === scope),
      answeredAt: all.answeredAt,
    };
  }

  /** Re-read the current scope's set in place; the old rows stay up until the new ones arrive. */
  async refresh(): Promise<void> {
    const scope = this.scope;
    if (!scope) return;
    const serial = ++this.requestSerial;
    this.loading = true;
    try {
      // Across work areas, and a namespace, are the main connection's question until F-0379 merges
      // every connection's answer; one work area asks the deployment that holds it (I-1120).
      const next: unknown = scope.startsWith(NAMESPACE_SCOPE_PREFIX)
        ? await tasksApiFor(null).waitingForMeInNamespace(
            scope.slice(NAMESPACE_SCOPE_PREFIX.length),
          )
        : scope === ALL_WORK_AREAS_SCOPE
          ? await tasksApiFor(null).waitingForMeEverywhere()
          : await tasksApiFor(scope).waitingForMe(scope);
      if (!Array.isArray(next))
        throw new Error('The server answered without the tasks waiting for you.');
      runInAction(() => {
        if (serial !== this.requestSerial || this.scope !== scope) return;
        this.waiting = next as ITask[];
        this.failure = null;
        this.answeredAt = new Date().toISOString();
        this.lastSets.set(scope, { waiting: this.waiting, answeredAt: this.answeredAt });
      });
    } catch (error: unknown) {
      runInAction(() => {
        if (serial !== this.requestSerial || this.scope !== scope) return;
        this.failure = describeLoadFailure(error);
      });
    } finally {
      runInAction(() => {
        if (serial === this.requestSerial) this.loading = false;
      });
    }
  }

  /**
   * I-0958 (WS-3): entering another work area drops this set before the page renders, so no row of
   * the previous one is drawn under the new one. The same work area keeps it.
   */
  enterWorkArea(workAreaId: string): void {
    if (this.scope === workAreaId) return;
    // RD-25: rows known to be this work area's stay up while it is read again in place.
    if (this.carriedInto(workAreaId)) {
      void this.loadScope(workAreaId);
      return;
    }
    this.dropSet();
  }

  /** Everything, the sets remembered per scope too. */
  reset(): void {
    this.dropSet();
    this.lastSets.clear();
  }

  private dropSet(): void {
    this.scope = null;
    this.waiting = null;
    this.failure = null;
    this.answeredAt = null;
    this.loading = false;
    this.requestSerial += 1;
  }

  dispose(): void {
    this.unsubscribeAttention?.();
    this.unsubscribeAttention = null;
    this.poller?.dispose();
    this.poller = null;
  }
}
